// Shop Alpine.js component

import { shopService } from './shop.service.js';
import { inventoryService } from './inventory.service.js';

export const shop = () => ({
  // State
  shopInfo: null,
  shopItems: [],
  playerItems: [],
  loading: false,
  error: null,
  
  // Filters
  selectedCategory: 'all',
  selectedRarity: 'all',
  selectedType: 'all',
  
  // Available filter options
  availableCategories: [],
  availableRarities: [],
  availableTypes: [],
  
  // UI State
  activeView: 'buy', // 'buy' or 'sell'
  showConfirmDialog: false,
  confirmAction: null,
  
  // Initialize component
  init() {
    console.log('Shop component initialized');
    this.loadShop();
    
    // Listen for location changes with null safety
    this.$watch('$store.app.p().location', () => {
      if (this.$store.app.hasPlayer()) {
        this.loadShop();
      }
    });

    // Subscribe to automatic shop updates
    this.unsubscribeFromUpdates = shopService.onUpdate(() => {
      // Only refresh if shop is currently visible and not loading
      if (!this.loading && this.shopInfo && this.activeView) {
        console.log('Auto-refreshing shop due to updates');
        this.loadShop();
      }
    });
  },
  
  // Load shop data for current location
  loadShop() {
    this.loading = true;
    this.error = null;
    
    try {
      const currentLocation = this.$store.app.p().location;
      
      // Check if location has a shop
      this.shopInfo = shopService.getShopInfo(currentLocation);
      
      if (!this.shopInfo) {
        this.error = 'No hay tienda en esta ubicación';
        this.shopItems = [];
        this.availableCategories = [];
        this.availableRarities = [];
        this.availableTypes = [];
        return;
      }
      
      // Load shop inventory and available options
      this.loadShopInventory();
      this.loadFilterOptions();
      this.loadPlayerItems();
      
      console.log(`Loaded shop: ${this.shopInfo.name}`);
      
    } catch (error) {
      console.error('Failed to load shop:', error);
      this.error = error.message;
    } finally {
      this.loading = false;
    }
  },
  
  // Load shop inventory with current filters
  loadShopInventory() {
    const currentLocation = this.$store.app.p().location;
    const filters = this.getActiveFilters();
    
    this.shopItems = shopService.getShopInventory(currentLocation, filters);
  },
  
  // Load filter options
  loadFilterOptions() {
    const currentLocation = this.$store.app.p().location;
    
    this.availableCategories = ['all', ...shopService.getShopCategories(currentLocation)];
    this.availableRarities = ['all', ...shopService.getShopRarities(currentLocation)];
    this.availableTypes = ['all', ...shopService.getShopTypes(currentLocation)];
  },
  
  // Load player items for selling
  loadPlayerItems() {
    try {
      const inventory = inventoryService.getInventoryState();
      this.playerItems = inventory.slots
        .filter(slot => slot !== null)
        .map(slot => ({
          ...inventoryService.getItemData(slot),
          quantity: slot.quantity,
          slotIndex: inventory.slots.indexOf(slot)
        }));
    } catch (error) {
      console.error('Failed to load player items:', error);
      this.playerItems = [];
    }
  },
  
  // Get active filters object
  getActiveFilters() {
    const filters = {};
    
    if (this.selectedCategory !== 'all') {
      filters.category = this.selectedCategory;
    }
    if (this.selectedRarity !== 'all') {
      filters.rarity = this.selectedRarity;
    }
    if (this.selectedType !== 'all') {
      filters.type = this.selectedType;
    }
    
    return filters;
  },
  
  // Switch between buy and sell views
  switchView(view) {
    if (this.activeView === view) return;
    
    this.activeView = view;
    
    if (view === 'buy') {
      this.loadShopInventory();
    } else {
      this.loadPlayerItems();
    }
    
    console.log(`Switched to ${view} view`);
  },
  
  // Apply filters and reload inventory
  applyFilters() {
    if (this.activeView === 'buy') {
      this.loadShopInventory();
    }
  },
  
  // Clear all filters
  clearFilters() {
    this.selectedCategory = 'all';
    this.selectedRarity = 'all';
    this.selectedType = 'all';
    this.applyFilters();
  },
  
  // Buy item from shop
  async buyItem(item, quantity = 1) {
    if (this.loading) return;
    
    try {
      const currentLocation = this.$store.app.p().location;
      const playerMoney = this.$store.app.p().money;
      const totalPrice = item.price * quantity;
      
      // Check if player can afford
      if (!shopService.canAfford(playerMoney, item.price, quantity)) {
        this.$store.app.addNotification(
          `No tienes suficiente dinero. Necesitas ${totalPrice} monedas`,
          'error'
        );
        return;
      }
      
      // Check inventory space
      if (!inventoryService.canAddItem(item.id, quantity)) {
        this.$store.app.addNotification(
          'No tienes suficiente espacio en el inventario',
          'error'
        );
        return;
      }
      
      // Show confirmation for expensive items
      if (totalPrice > 100) {
        this.confirmPurchase(item, quantity);
        return;
      }
      
      // Execute purchase
      this.executePurchase(item, quantity);
      
    } catch (error) {
      console.error('Purchase failed:', error);
      this.$store.app.addNotification(error.message, 'error');
    }
  },
  
  // Confirm expensive purchase
  confirmPurchase(item, quantity) {
    const totalPrice = item.price * quantity;
    
    this.confirmAction = {
      type: 'buy',
      title: 'Confirmar Compra',
      message: `¿Comprar ${quantity}x ${item.name} por ${totalPrice} monedas?`,
      confirmText: 'Comprar',
      confirmClass: 'btn-primary',
      item,
      quantity
    };
    
    this.showConfirmDialog = true;
  },
  
  // Execute confirmed purchase
  async executePurchase(item, quantity) {
    try {
      this.loading = true;
      
      const currentLocation = this.$store.app.p().location;
      const transaction = shopService.buyItem(currentLocation, item.id, quantity);
      
      // Add item to inventory
      inventoryService.addItem(item.id, quantity);
      
      // Deduct money from player
      this.$store.app.updatePlayer({
        money: this.$store.app.p().money - transaction.totalPrice
      });
      
      this.$store.app.addNotification(
        `Compraste ${quantity}x ${item.name} por ${transaction.totalPrice} monedas`,
        'success'
      );
      
      // Reload shop inventory
      this.loadShopInventory();
      
    } catch (error) {
      console.error('Purchase execution failed:', error);
      this.$store.app.addNotification(error.message, 'error');
    } finally {
      this.loading = false;
    }
  },
  
  // Sell item to shop
  async sellItem(item, quantity = 1) {
    if (this.loading) return;
    
    try {
      const currentLocation = this.$store.app.p().location;
      
      // Calculate sell price
      const transaction = shopService.sellItem(currentLocation, item.id, quantity);
      
      // Show confirmation
      this.confirmSale(item, quantity, transaction);
      
    } catch (error) {
      console.error('Sale failed:', error);
      this.$store.app.addNotification(error.message, 'error');
    }
  },
  
  // Confirm sale
  confirmSale(item, quantity, transaction) {
    this.confirmAction = {
      type: 'sell',
      title: 'Confirmar Venta',
      message: `¿Vender ${quantity}x ${item.name} por ${transaction.totalPrice} monedas?`,
      confirmText: 'Vender',
      confirmClass: 'btn-success',
      item,
      quantity,
      transaction
    };
    
    this.showConfirmDialog = true;
  },
  
  // Execute confirmed sale
  async executeSale(item, quantity, transaction) {
    try {
      this.loading = true;
      
      // Remove item from inventory
      const playerItem = this.playerItems.find(p => p.id === item.id);
      inventoryService.removeItem(playerItem.slotIndex, quantity);
      
      // Add money to player
      this.$store.app.updatePlayer({
        money: this.$store.app.p().money + transaction.totalPrice
      });
      
      this.$store.app.addNotification(
        `Vendiste ${quantity}x ${item.name} por ${transaction.totalPrice} monedas`,
        'success'
      );
      
      // Reload player items
      this.loadPlayerItems();
      
    } catch (error) {
      console.error('Sale execution failed:', error);
      this.$store.app.addNotification(error.message, 'error');
    } finally {
      this.loading = false;
    }
  },
  
  // Execute confirmed action
  async executeConfirmedAction() {
    if (!this.confirmAction) return;
    
    const { type, item, quantity, transaction } = this.confirmAction;
    
    this.closeConfirmDialog();
    
    if (type === 'buy') {
      await this.executePurchase(item, quantity);
    } else if (type === 'sell') {
      await this.executeSale(item, quantity, transaction);
    }
  },
  
  // Close confirmation dialog
  closeConfirmDialog() {
    this.showConfirmDialog = false;
    this.confirmAction = null;
  },
  
  // Get item card class
  getItemCardClass(item) {
    const baseClass = 'item-card';
    return `${baseClass} rarity-${item.rarity}`;
  },
  
  // Get view button class
  getViewButtonClass(view) {
    const baseClass = 'view-button';
    return this.activeView === view ? `${baseClass} active` : baseClass;
  },
  
  // Format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat('es-ES').format(amount);
  },
  
  // Check if player can afford item
  canAffordItem(item, quantity = 1) {
    if (!this.$store.app?.hasPlayer?.()) return false;
    const playerMoney = this.$store.app.p().money;
    return shopService.canAfford(playerMoney, item.price, quantity);
  },
  
  // Get sellable items (items shop accepts)
  getSellableItems() {
    if (!this.shopInfo) return [];
    
    // Filter items that can be sold to this shop
    return this.playerItems.filter(item => {
      try {
        const currentLocation = this.$store.app.p().location;
        shopService.sellItem(currentLocation, item.id, 1);
        return true;
      } catch (error) {
        return false;
      }
    });
  },
  
  // Calculate sell price for display
  getSellPrice(item) {
    try {
      const currentLocation = this.$store.app.p().location;
      const transaction = shopService.sellItem(currentLocation, item.id, 1);
      return transaction.unitPrice;
    } catch (error) {
      return 0;
    }
  },
  
  // Format time until restock
  formatRestockTime() {
    if (!this.shopInfo || this.shopInfo.restockHours <= 0) {
      return 'Disponible';
    }
    
    if (this.shopInfo.restockHours >= 24) {
      const days = Math.floor(this.shopInfo.restockHours / 24);
      const hours = this.shopInfo.restockHours % 24;
      return `${days}d ${hours}h`;
    } else {
      return `${this.shopInfo.restockHours}h`;
    }
  },
  
  // Refresh shop data
  refresh() {
    this.loadShop();
  },
  
  // Cleanup when component is destroyed
  destroy() {
    this.showConfirmDialog = false;
    this.confirmAction = null;
    
    // Unsubscribe from shop updates
    if (this.unsubscribeFromUpdates) {
      this.unsubscribeFromUpdates();
      this.unsubscribeFromUpdates = null;
    }
    
    console.log('Shop component destroyed');
  }
});